import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Modal from 'react-bootstrap/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { useParams , useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Alert from '@mui/material/Alert'
import BreadcrumbsWrapper from '../../components/breadcrumbs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// =========================== MODAL ===========================
function MyVerticallyCenteredModal({ show, onHide, authToken, onCustomerAdd }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async () => {
    try {
      setLoading(true);
      const res = await postRequest('private/createCustomer', searchFields, authToken);
      if (res?.status === 200 || res?.status === 'success' || (res?.data && res.data._id)) {
        Swal.fire('Success', 'Customer created successfully!', 'success');
        const newCustomer = res.data;
        onCustomerAdd(newCustomer);
        onHide();
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });
      } else {
        Swal.fire('Error', res?.message || 'Failed to create customer', 'error');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Customers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleCreateCustomer}
                disabled={loading || !searchFields.forename || !searchFields.surname || !searchFields.email}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': { backgroundColor: '#5a34a0' },
                  textTransform: 'uppercase',
                  fontWeight: 'bold'
                }}
              >
                Add Customer
              </Button>
            </Box>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

// ======================= MAIN COMPONENT ========================
export const LsseStateAdmins = () => {
  const [customerData, setCustomerData] = useState(null);
  const [groupCodes, setGroupCodes] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [customerError, setCustomerError] = useState(false); // ✅ for error on empty customer selection
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [lssEstateCosts, setLssEstateCosts] = useState(null); // LSS Estate Administration rate card
  const [fetchedProductId, setFetchedProductId] = useState(null);

  const { customerId, slug } = useParams();
  const location = useLocation()
  const authCtx = useContext(AuthContext);
  const productId = location.state?.productId;

  // Debug logging
  console.log('🔍 LSS Estate Administration - Debug Info:');
  console.log('- Product ID:', productId);
  console.log('- Slug:', slug);
  console.log('- Location State:', location.state);

  // Get customerId from URL query params
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId');
  const navigate = useNavigate();

           const slugToTitle = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  const readableTitle = slugToTitle(slug || '');

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      group_code: '',
      acquisition: 'Inbound',
      net_value: '',
      lender_panel_id: '',
      partner_id: '',
    },
    mode: 'onTouched',
  });

  const handleCustomerAdd = (customer) => {
    if (!selectedCustomers.some(c => c._id === customer._id)) {
      setSelectedCustomers(prev => [...prev, customer]);
      setCustomerError(false); // ✅ reset error
    }
  };

  const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setGroupCodes(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch group codes:", err);
    }
  };

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch product ID based on slug if not available in location.state
  const fetchProductId = async () => {
    try {
      console.log('📋 Fetching product ID for slug:', slug);
      const response = await postRequest('private/product-list', {}, authCtx.token);

      if (response?.data?.docs) {
        // Find LSS Estate Administration product by slug or title
        const product = response.data.docs.find(p =>
          p.slug?.toLowerCase().includes('lss') &&
          p.slug?.toLowerCase().includes('estate') &&
          p.slug?.toLowerCase().includes('administration')
        );

        if (product) {
          console.log('✅ Found product:', product);
          setFetchedProductId(product._id);
          return product._id;
        } else {
          console.error('❌ LSS Estate Administration product not found');
        }
      }
    } catch (error) {
      console.error('❌ Error fetching product ID:', error);
    }
    return null;
  };

  // Fetch LSS Estate Administration rate card from Manchester branch
  const fetchLssEstateAdministrationCosts = async (prodId) => {
    if (!prodId) {
      console.log('⚠️ No productId available yet');
      return;
    }

    try {
      console.log('📋 Fetching LSS Estate Administration rate card for product:', prodId);

      const response = await postRequest('private/getLssEstateAdministrationCost', {
        product_id: prodId
      }, authCtx.token);

      if (response?.status === 200 && response?.data) {
        console.log('✅ LSS Estate Administration rate card fetched:', response.data);
        setLssEstateCosts(response.data);
        Swal.fire({
          icon: 'success',
          title: 'Rate Card Loaded',
          text: 'LSS Estate Administration rate card has been loaded from Manchester branch.',
          timer: 2000,
          showConfirmButton: false
        });
      } else if (response?.status === 407) {
        console.warn('⚠️ Rate card not configured for Manchester branch');
        setLssEstateCosts(null);
        Swal.fire({
          icon: 'warning',
          title: 'Rate Card Not Configured',
          text: 'LSS Estate Administration rate card is not configured for Manchester branch. Please configure it in Branch Management first.',
          confirmButtonColor: '#6f42c1'
        });
      } else if (response?.status === 404) {
        console.error('❌ Manchester branch not found');
        setLssEstateCosts(null);
        Swal.fire({
          icon: 'error',
          title: 'Manchester Branch Not Found',
          text: 'Manchester branch is not configured in the system. Please contact administrator.',
          confirmButtonColor: '#6f42c1'
        });
      } else {
        console.error('❌ Failed to fetch LSS Estate Administration rate card:', response);
        setLssEstateCosts(null);
      }
    } catch (error) {
      console.error('❌ Error fetching LSS Estate Administration rate card:', error);
      setLssEstateCosts(null);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch rate card. Please try again or contact administrator.',
        confirmButtonColor: '#6f42c1'
      });
    }
  };

  const fetchCustomerDetails = async (id) => {
    try {
      const response = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setCustomerData(response.data);
      }
    } catch (err) {
      console.error('Error fetching customer details', err);
    }
  };

  useEffect(() => {
    fetchGroupCodes();
    if (customerId) fetchCustomerDetails(customerId);
  }, [customerId]);

  // Fetch active lender panels and partners on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  // Fetch LSS Estate Administration rate card when productId is available
  useEffect(() => {
    const loadRateCard = async () => {
      if (!authCtx.token) return;

      let prodId = productId || fetchedProductId;

      // If no productId available, fetch it first
      if (!prodId) {
        console.log('⚠️ No productId in state, fetching from product list...');
        prodId = await fetchProductId();
      }

      // Now fetch rate card with the productId
      if (prodId) {
        await fetchLssEstateAdministrationCosts(prodId);
      }
    };

    loadRateCard();
  }, [productId, fetchedProductId, authCtx.token]);

  // Fetch initial customer from URL and add to selectedCustomers
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      if (customerIdFromUrl && authCtx.token && selectedCustomers.length === 0) {
        console.log('Fetching initial customer from URL:', customerIdFromUrl);
        try {
          const customerRes = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);
          if (customerRes?.data) {
            console.log('Fetched initial customer:', customerRes.data);
            setSelectedCustomers([customerRes.data]);
            setCustomerError(false);
          }
        } catch (err) {
          console.error('Error fetching initial customer:', err);
        }
      }
    };

    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token]);

  const onSubmit = async (formData) => {
   
    if (selectedCustomers.length === 0) {
      setCustomerError(true);
      return;
    }

    const created_id = localStorage.getItem('id')

    const payload = {
      ...formData,
      product_id:productId,
      created_id:created_id,
      customer_ids: selectedCustomers.map(c => c._id),
      lender_panel_id: formData.lender_panel_id || null,
      partner_id: formData.partner_id || null,
    };

    console.log('🚀 LSS ESTATE ADMIN - PAYLOAD BEING SENT:');
    console.log('  - partner_id from formData:', formData.partner_id);
    console.log('  - partner_id in payload:', payload.partner_id);
    console.log('  - Full payload:', payload);

    try {
      const res = await postRequest('private/createLeads', payload, authCtx.token);
      if (res?.status == 'success') {
        Swal.fire('Success', 'Leads created successfully!', 'success');
        reset();
        setSelectedCustomers([]);
        navigate('/leads');
      } else {
        Swal.fire('Error', res?.message || 'Failed to create leads', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
       items={[
       { label: 'Dashboard', path: '/dashboard' },
        { label: 'Customers', path: '/customers' },
        { label: 'Lss Estate Adminstration', path: '' },
          ]}
            />
           <br></br>

      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Create {readableTitle}
          <small className='color-grey'> {customerData ? `${customerData.forename || ''} ${customerData.surname || ''}` : ''}</small>
        </h1>
      </div>

      <div className='page-top-formview mb-3'>
        <Form onSubmit={handleSubmit(onSubmit)}>

          <Row className="mb-3">
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Group Code</h5>
              <Controller
                name="group_code"
                control={control}
                rules={{ required: 'Group Code is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.group_code}>
                    <InputLabel>Group Code</InputLabel>
                    <Select {...field} label="Group Code">
                      {groupCodes.map((item) => (
                        <MenuItem key={item.code} value={item._id}>
                          {item.name} ({item.code})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.group_code && (
                      <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>{errors.group_code.message}</p>
                    )}
                  </FormControl>
                )}
              />
            </Col>

            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Acquisition</h5>
              <Controller
                name="acquisition"
                control={control}
                rules={{ required: 'Acquisition is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.acquisition}>
                    <InputLabel>Acquisition</InputLabel>
                    <Select {...field} label="Acquisition">
                      <MenuItem value='Inbound'>Inbound</MenuItem>
                      <MenuItem value='Cold-Acquistion'>Cold-Acquistion</MenuItem>
                    </Select>
                    {errors.acquisition && (
                      <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>{errors.acquisition.message}</p>
                    )}
                  </FormControl>
                )}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Net Estate Value</h5>
              <Controller
                name="net_value"
                control={control}
                rules={{
                  required: 'Net Estate Value is required',
                  validate: val => val > 0 || 'Value must be greater than 0'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Net Estate Value"
                    sx={{ width: '100%' }}
                    type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    }}
                    error={!!errors.net_value}
                    helperText={errors.net_value?.message}
                  />
                )}
              />
            </Col>

            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Override Date (Optional)</h5>
              <Controller
                name="override_date"
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Override Date"
                      sx={{ width: '100%' }}
                      {...field}
                      value={field.value || null}
                      onChange={(date) => field.onChange(date)}
                    />
                  </LocalizationProvider>
                )}
              />
            </Col>
          </Row>

          {/* Lender Panel & Partner Section */}
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Lender Panel</h5>
              <Controller
                name="lender_panel_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{ backgroundColor: 'white' }}
                    >
                      <MenuItem value="">
                        <em>Select Lender Panel (Optional)</em>
                      </MenuItem>
                      {lenderPanels.map((panel) => (
                        <MenuItem key={panel._id} value={panel._id}>
                          {panel.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Col>
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Partner</h5>
              <Controller
                name="partner_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{ backgroundColor: 'white' }}
                    >
                      <MenuItem value="">
                        <em>Select Partner (Optional)</em>
                      </MenuItem>
                      {partners.map((partner) => (
                        <MenuItem key={partner._id} value={partner._id}>
                          {partner.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Customers</h5>
              {customerError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  Please select at least one customer.
                </Alert>
              )}
              {selectedCustomers.length > 0 && (
                selectedCustomers.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: 'white'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer.title} {customer.forename} {customer.surname}
                      </Typography>
                      {customer.email && (
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          📧 {customer.email}
                        </Typography>
                      )}
                      {customer.phone_number && (
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          📞 {customer.phone_number}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedCustomers((prev) =>
                          prev.filter((c) => c._id !== customer._id)
                        )
                      }
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))
              )}
              <Button
                variant="text"
                startIcon={<PersonAddOutlinedIcon />}
                onClick={() => setModalShow(true)}
                sx={{
                  color: '#6f42c1',
                  textTransform: 'uppercase',
                  fontWeight: 'normal',
                  justifyContent: 'flex-start',
                  pl: 0
                }}
              >
                Add Customer
              </Button>
            </Col>
            <MyVerticallyCenteredModal
              show={modalShow}
              onHide={() => setModalShow(false)}
              authToken={authCtx.token}
              onCustomerAdd={handleCustomerAdd}
            />
          </Row>

          <Row>
            <Form.Group as={Col}>
              <FormControl fullWidth sx={{ my: 1 }}>
                <Stack spacing={2} direction="row">
                  <Button variant="contained" type="submit" className='red-bg red-button text-capitalize h-56'>Submit</Button>
                </Stack>
              </FormControl>
            </Form.Group>
          </Row>
        </Form>
      </div>
    </>
  );
};
